/**
 * Global SEO Middleware
 * ทำหน้าที่ดึงข้อมูลสำหรับ SEO meta data ในช่วง SSR
 * จะทำงานกับทุก route และดึงข้อมูลตาม path parameters
 */

// Import จำเป็นสำหรับ Nuxt 3
import { useNuxtApp, useState, useHead, defineNuxtRouteMiddleware } from '#imports';

export default defineNuxtRouteMiddleware(async (to, from) => {
  console.log('[SEO Middleware] Called for route:', to.path, 'SSR:', process.server);
  
  // ทำงานเฉพาะใน SSR เท่านั้น
  if (!process.server) {
    console.log('[SEO Middleware] Skipping - not SSR');
    return;
  }

  // Skip ใน development mode เพื่อป้องกัน API error
  if (process.dev || process.env.NODE_ENV === 'development') {
    console.log('[SEO Middleware] Skipping in development mode');
    return;
  }

  console.log('[SEO Middleware] Starting SEO data fetch for:', to.path);

  try {
    // ดึง hostname จาก SSR context
    const nuxtApp = useNuxtApp();
    const hostname = getHostnameFromSSRContext(nuxtApp);
    
    // สร้าง page config request ตาม route
    const pageConfigData = await fetchPageConfigForSEO(to, hostname);
    
    if (pageConfigData) {
      // เก็บข้อมูลใน state สำหรับ SSR hydration
      const pageConfig = useState('pageConfig', () => null);
      const pageMetaData = useState('pageMetaData', () => ({
        title: null,
        description: null,
        keywords: null,
        image: null,
        url: null
      }));
      
      pageConfig.value = pageConfigData;
      
      // สร้าง meta data จาก page config
      const metaData = generateMetaDataFromConfig(pageConfigData, to, hostname);
      pageMetaData.value = metaData;
      
      console.log('[SEO Middleware] Meta data generated:', metaData);
      
      // ตั้งค่า page head สำหรับ SEO
      useHead({
        title: metaData.title,
        meta: [
          { name: 'description', content: metaData.description },
          { name: 'keywords', content: metaData.keywords },
          { property: 'og:title', content: metaData.title },
          { property: 'og:description', content: metaData.description },
          { property: 'og:image', content: metaData.image },
          { property: 'og:url', content: metaData.url },
          { name: 'twitter:card', content: 'summary_large_image' },
          { name: 'twitter:title', content: metaData.title },
          { name: 'twitter:description', content: metaData.description },
          { name: 'twitter:image', content: metaData.image }
        ]
      });
    }
    
  } catch (error) {
    console.error('[SEO Middleware] Error fetching page config:', error);
    // ไม่ throw error เพื่อไม่ให้ block การทำงานของแอป
  }
});

/**
 * ดึง hostname จาก SSR context
 */
function getHostnameFromSSRContext(nuxtApp) {
  try {
    const req = nuxtApp.ssrContext?.event?.req;
    if (req) {
      let hostname = req.headers.host?.split(':')[0] || 'frontend.cloudrestfulapi.com';
      
      // Handle localhost และ development environments
      if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('frontend.dev.test')) {
        hostname = 'doa.fti.or.th';
      }
      
      if (hostname === 'frontend.cloudrestfulapi.com') {
        hostname = 'fti.academy';
      }
      
      return hostname;
    }
  } catch (error) {
    console.warn('[SEO Middleware] Could not get hostname from SSR context:', error);
  }
  
  return 'frontend.cloudrestfulapi.com';
}

/**
 * ดึงข้อมูล page config สำหรับ SEO ตาม route
 */
async function fetchPageConfigForSEO(route, hostname) {
  const startTime = Date.now();
  
  try {
    // สร้าง URL สำหรับ page config API
    const configUrl = buildPageConfigUrl(route, hostname);
    
    console.log('[SEO Middleware] Fetching page config from:', configUrl);
    
    // เตรียม headers สำหรับ request
    const headers = {
      'Content-Type': 'application/json',
      'X-Environment': 'production',
      'X-Page-Path': route.path,
      'X-Route-Name': route.name || 'unknown'
    };
    
    // เพิ่ม cookies จาก SSR context
    try {
      const nuxtApp = useNuxtApp();
      const event = nuxtApp.ssrContext?.event;
      const cookieHeader = event?.node?.req?.headers?.cookie;
      
      if (cookieHeader) {
        headers['Cookie'] = cookieHeader;
      }
    } catch (error) {
      // Silently handle cookie access issues
    }
    
    const response = await fetch(configUrl, {
      method: 'GET',
      headers,
      // timeout สำหรับ SSR
      signal: AbortSignal.timeout(5000)
    });
    
    if (!response.ok) {
      throw new Error(`Page config fetch failed: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    const duration = Date.now() - startTime;
    
    console.log(`[SEO Middleware] Page config loaded in ${duration}ms:`, data);
    
    return data;
    
  } catch (error) {
    const duration = Date.now() - startTime;
    console.error(`[SEO Middleware] Page config fetch failed after ${duration}ms:`, error);
    
    // Return route-based fallback config สำหรับ SEO
    return generateRouteBasedSEOConfig(route, hostname);
  }
}

/**
 * สร้าง SEO config ตาม route เมื่อ API ไม่ทำงาน
 */
function generateRouteBasedSEOConfig(route, hostname) {
  console.log('[SEO Middleware] Generating route-based SEO config for:', route.path);
  
  const routeConfig = getRouteSpecificConfig(route);
  
  return {
    title: routeConfig.title,
    description: routeConfig.description,
    keywords: routeConfig.keywords,
    image: routeConfig.image,
    type: routeConfig.type,
    siteName: hostname,
    source: 'route-fallback'
  };
}

/**
 * กำหนด SEO config ตาม route patterns
 */
function getRouteSpecificConfig(route) {
  const path = route.path;
  const name = route.name;
  const params = route.params;
  
  // Homepage
  if (path === '/' || name === 'index') {
    return {
      title: 'หน้าแรก',
      description: 'ยินดีต้อนรับสู่แพลตฟอร์มการศึกษาออนไลน์ของเรา',
      keywords: 'การศึกษา, ออนไลน์, คอร์สเรียน, การเรียนรู้',
      image: '/images/homepage-og.jpg',
      type: 'website'
    };
  }
  
  // Cluster pages
  if (name && name.includes('cluster')) {
    const clusterId = params.clusterId || 'default';
    
    if (name.includes('dashboard')) {
      return {
        title: `แดชบอร์ด - Cluster ${clusterId}`,
        description: `แดชบอร์ดการจัดการสำหรับ Cluster ${clusterId}`,
        keywords: `แดชบอร์ด, จัดการ, cluster, ${clusterId}`,
        image: '/images/dashboard-og.jpg',
        type: 'website'
      };
    }
    
    if (name.includes('user')) {
      return {
        title: `จัดการผู้ใช้ - Cluster ${clusterId}`,
        description: `หน้าจัดการผู้ใช้งานในระบบ Cluster ${clusterId}`,
        keywords: `ผู้ใช้, จัดการ, cluster, ${clusterId}`,
        image: '/images/users-og.jpg',
        type: 'website'
      };
    }
  }
  
  // User profile pages
  if (name && name.includes('user')) {
    const userId = params.userId || params.id || 'profile';
    
    return {
      title: `โปรไฟล์ผู้ใช้ - ${userId}`,
      description: `ข้อมูลโปรไฟล์และการตั้งค่าของผู้ใช้ ${userId}`,
      keywords: `โปรไฟล์, ผู้ใช้, ${userId}, ข้อมูลส่วนตัว`,
      image: '/images/profile-og.jpg',
      type: 'profile'
    };
  }
  
  // Product pages
  if (name && name.includes('product')) {
    const productId = params.productId || params.id || 'catalog';
    
    if (name.includes('detail')) {
      return {
        title: `รายละเอียดสินค้า - ${productId}`,
        description: `ข้อมูลรายละเอียดของสินค้า ${productId}`,
        keywords: `สินค้า, รายละเอียด, ${productId}, ซื้อขาย`,
        image: '/images/product-og.jpg',
        type: 'product'
      };
    }
    
    return {
      title: 'แคตตาล็อกสินค้า',
      description: 'รายการสินค้าทั้งหมดในระบบ',
      keywords: 'สินค้า, แคตตาล็อก, รายการสินค้า',
      image: '/images/catalog-og.jpg',
      type: 'website'
    };
  }
  
  // Order pages
  if (name && name.includes('order')) {
    const orderId = params.orderId || params.id || 'list';
    
    if (name.includes('detail')) {
      return {
        title: `รายละเอียดคำสั่งซื้อ - ${orderId}`,
        description: `ข้อมูลรายละเอียดของคำสั่งซื้อ ${orderId}`,
        keywords: `คำสั่งซื้อ, รายละเอียด, ${orderId}, สถานะ`,
        image: '/images/order-og.jpg',
        type: 'website'
      };
    }
    
    return {
      title: 'รายการคำสั่งซื้อ',
      description: 'รายการคำสั่งซื้อทั้งหมดของผู้ใช้',
      keywords: 'คำสั่งซื้อ, รายการ, ประวัติการสั่งซื้อ',
      image: '/images/orders-og.jpg',
      type: 'website'
    };
  }
  
  // About page
  if (path.includes('about')) {
    return {
      title: 'เกี่ยวกับเรา',
      description: 'ข้อมูลเกี่ยวกับบริษัทและทีมงานของเรา',
      keywords: 'เกี่ยวกับเรา, บริษัท, ทีมงาน, ข้อมูลบริษัท',
      image: '/images/about-og.jpg',
      type: 'website'
    };
  }
  
  // Contact page
  if (path.includes('contact')) {
    return {
      title: 'ติดต่อเรา',
      description: 'ช่องทางการติดต่อและข้อมูลการติดต่อของเรา',
      keywords: 'ติดต่อเรา, ช่องทางติดต่อ, สอบถาม',
      image: '/images/contact-og.jpg',
      type: 'website'
    };
  }
  
  // Generic fallback based on path
  const pathSegments = path.split('/').filter(Boolean);
  const pageTitle = pathSegments.length > 0 
    ? pathSegments.map(segment => {
        // แปลง slug เป็น title
        return segment
          .replace(/-/g, ' ')
          .replace(/\b\w/g, l => l.toUpperCase());
      }).join(' - ')
    : 'หน้าหลัก';
  
  return {
    title: pageTitle,
    description: `หน้า ${pageTitle} ในระบบของเรา`,
    keywords: `${pageTitle.toLowerCase()}, ระบบ, เว็บไซต์`,
    image: '/images/default-og.jpg',
    type: 'website'
  };
}

/**
 * สร้าง URL สำหรับ page config API ตาม route
 */
function buildPageConfigUrl(route, hostname) {
  // Base API URL
  const baseUrl = 'https://multi-tenants.manonsanoi.workers.dev';
  
  // สร้าง URL ตาม route pattern
  if (route.name === 'index') {
    // Homepage
    return `${baseUrl}/page-config/${hostname}/home`;
  } else if (route.name && route.name.includes('cluster')) {
    // Cluster pages
    const clusterId = route.params.clusterId || 'default';
    const section = route.name.split('-').pop() || 'dashboard';
    return `${baseUrl}/page-config/${hostname}/cluster/${clusterId}/${section}`;
  } else if (route.name && route.name.includes('user')) {
    // User pages
    const userId = route.params.userId || route.params.id || 'profile';
    const section = route.name.split('-').pop() || 'profile';
    return `${baseUrl}/page-config/${hostname}/user/${userId}/${section}`;
  } else if (route.name && route.name.includes('product')) {
    // Product pages
    const productId = route.params.productId || route.params.id || 'catalog';
    const section = route.name.split('-').pop() || 'detail';
    return `${baseUrl}/page-config/${hostname}/product/${productId}/${section}`;
  } else if (route.name && route.name.includes('order')) {
    // Order pages
    const orderId = route.params.orderId || route.params.id || 'list';
    const section = route.name.split('-').pop() || 'detail';
    return `${baseUrl}/page-config/${hostname}/order/${orderId}/${section}`;
  } else {
    // Generic page config
    const pathSegments = route.path.split('/').filter(Boolean);
    const configPath = pathSegments.length > 0 ? pathSegments.join('/') : 'default';
    return `${baseUrl}/page-config/${hostname}/${configPath}`;
  }
}

/**
 * สร้าง meta data จาก page config
 */
function generateMetaDataFromConfig(pageConfig, route, hostname) {
  const baseUrl = `https://${hostname}`;
  const currentUrl = `${baseUrl}${route.fullPath}`;
  
  // Default values
  let title = pageConfig.title || 'Application';
  let description = pageConfig.description || 'Welcome to our application';
  let keywords = pageConfig.keywords || 'application, website';
  let image = pageConfig.image || pageConfig.ogImage || '/default-og-image.jpg';
  
  // ปรับปรุง title ตาม route parameters
  if (route.params && Object.keys(route.params).length > 0) {
    // แทนที่ placeholders ใน title
    Object.keys(route.params).forEach(param => {
      const value = route.params[param];
      const placeholder = `{${param}}`;
      
      if (title.includes(placeholder)) {
        title = title.replace(placeholder, value);
      }
      if (description.includes(placeholder)) {
        description = description.replace(placeholder, value);
      }
    });
  }
  
  // เพิ่ม site name ใน title (ยกเว้นหากมีอยู่แล้ว)
  const siteName = pageConfig.siteName || hostname;
  if (siteName && !title.includes(siteName) && !title.includes('|')) {
    title = `${title} | ${siteName}`;
  }
  
  // ตรวจสอบว่า image เป็น absolute URL หรือไม่
  if (image && !image.startsWith('http')) {
    image = `${baseUrl}${image}`;
  }
  
  // Log การสร้าง meta data
  console.log('[SEO Middleware] Generated meta data:', {
    title,
    description: description.substring(0, 100) + '...',
    source: pageConfig.source || 'api',
    routePath: route.path,
    routeName: route.name
  });
  
  return {
    title,
    description,
    keywords,
    image,
    url: currentUrl,
    type: pageConfig.type || 'website',
    siteName: siteName,
    source: pageConfig.source || 'api'
  };
}
